/**
 * Per-job-type "renderer summary" (RM-118). Modular: each job type owns its
 * summary renderer, dispatched on `job_spec.type`. Adding a new job type = one
 * new arm here (and its spec fields) — the table column stays type-agnostic.
 *
 * Rendered as a compact label→value mini-table (a two-column `<dl>` grid), not
 * an inline run of chips. Render modes are a Blender-only param, so they live
 * inside the Blender summary here — not as a top-level table column.
 */
import { type ReactNode } from 'react'
import type { Job } from '../../lib/schemas'
import { modeLabels } from '../../lib/jobView'

/** A row is dropped entirely when its value is null/undefined/''. */
type Rows = [label: string, value: ReactNode][]

function MiniTable({ rows }: { rows: Rows }) {
  const shown = rows.filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  )
  if (shown.length === 0)
    return <span className="text-xs text-muted-foreground">—</span>
  return (
    <div className="text-xs">
      {shown.map(([label, value], i) => (
        <div key={i} className="flex items-baseline gap-3">
          <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
          <span className="text-foreground tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  )
}

const str = (v: unknown) => (v == null ? undefined : String(v))

/** Blender render params live in the (passthrough) first mode_param. */
function BlenderSummary({
  job,
  modeParams,
}: {
  job: Job
  modeParams: Record<string, unknown>[]
}) {
  const mp = modeParams?.[0] ?? {}
  const engine = str(mp.engine)
  const w = str(mp.width)
  const h = str(mp.height)
  const fs = str(mp.frame_start)
  const fe = str(mp.frame_end)
  const samples = str(mp.samples)
  const modes = modeLabels(job)

  return (
    <MiniTable
      rows={[
        ['Tryby', modes.length > 0 ? modes.join(', ') : undefined],
        ['Silnik', engine],
        ['Rozdz.', w && h ? `${w}×${h}` : undefined],
        ['Klatki', fs && fe ? `${fs}–${fe}` : undefined],
        ['Sample', samples ? `${samples} spp` : undefined],
      ]}
    />
  )
}

export function JobSummary({ job }: { job: Job }) {
  const spec = job.job_spec
  switch (spec.type) {
    case 'surroundings':
      return <span className="text-xs">Generowanie otoczenia</span>
    case 'blender_render':
      return <BlenderSummary job={job} modeParams={spec.mode_params} />
    case 'upscaling':
      return (
        <MiniTable
          rows={[
            [
              'View',
              <span className="inline-block max-w-[14rem] truncate align-bottom font-mono">
                {spec.view_id}
              </span>,
            ],
            [
              'Upscaler',
              `${spec.main_upscaler}${
                spec.topaz_upscale_factor && spec.topaz_power
                  ? ` ·${spec.topaz_upscale_factor}×${spec.topaz_power}`
                  : ''
              } → ${spec.target_size ? `${spec.target_size}px` : 'natywna'}`,
            ],
            ['Overlay', spec.investment_upscaler ?? undefined],
            [
              'Feather',
              spec.merge_feather_px ? `${spec.merge_feather_px}px` : undefined,
            ],
          ]}
        />
      )
    case 'empty':
      return (
        <MiniTable
          rows={[
            ['Czekaj', `${spec.wait_secs}s`],
            ['Exit', spec.exit_code !== undefined ? spec.exit_code : undefined],
          ]}
        />
      )
    case 'turntable_frame_bake':
      return (
        <MiniTable
          rows={[
            ['Target', spec.output_target_id],
            ['Frames', spec.output_frame_count],
            [
              'Views',
              <span className="inline-block max-w-[14rem] truncate align-bottom font-mono">
                {spec.color_view_id} / {spec.depth_view_id}
              </span>,
            ],
          ]}
        />
      )
    default:
      return <span className="text-xs text-muted-foreground">—</span>
  }
}
